import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import InstallInquiryModal from "@/components/base/InstallInquiryModal";

const AREAS = [
  {
    region: "서울",
    districts: ["강남구", "서초구", "송파구", "강동구", "마포구", "용산구", "성동구", "광진구", "강서구", "양천구", "영등포구", "동작구", "관악구", "노원구", "도봉구", "강북구", "성북구", "종로구", "중구", "은평구", "서대문구", "중랑구", "동대문구", "구로구", "금천구"],
  },
  {
    region: "경기",
    districts: ["의정부시", "수원시", "성남시", "고양시", "용인시", "부천시", "안산시", "안양시", "남양주시", "화성시", "평택시", "의왕시", "군포시", "광명시", "시흥시", "파주시", "김포시", "광주시", "양주시", "구리시", "오산시", "하남시", "이천시", "안성시"],
  },
  {
    region: "인천",
    districts: ["중구", "동구", "미추홀구", "연수구", "남동구", "부평구", "계양구", "서구", "강화군", "옹진군"],
  },
];

const HeroSection = () => {
  const [showModal, setShowModal] = useState(false);
  const [activeArea, setActiveArea] = useState<string | null>(null);

  const openAIChat = () => {
    const event = new CustomEvent("doerjun:openChat");
    window.dispatchEvent(event);
  };

  const handleAreaClick = (region: string) => {
    setActiveArea((prev) => (prev === region ? null : region));
  };

  const activeDistricts = AREAS.find((a) => a.region === activeArea)?.districts ?? [];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/44c0d69ab8217227c1ad1c5a883be217.jpg"
          alt="도어락 히어로 이미지"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/60 to-white/20" />
      </div>

      {/* SNS 위젯 — 우측 고정 (데스크탑) */}
      <div className="absolute right-6 md:right-10 bottom-10 z-10 flex-col gap-2 hidden lg:flex">
        {/* 네이버 블로그 */}
        <a
          href="https://blog.naver.com/qordudwns12"
          target="_blank"
          rel="nofollow noreferrer"
          className="group flex items-center gap-0 hover:gap-3 overflow-hidden transition-all duration-300 bg-white/90 hover:bg-white border border-stone-200 rounded-full cursor-pointer"
          style={{ height: "40px", paddingLeft: "10px", paddingRight: "10px" }}
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-article-line text-emerald-600 text-base" />
          </div>
          <span className="text-xs font-bold text-stone-700 whitespace-nowrap max-w-0 group-hover:max-w-xs overflow-hidden transition-all duration-300 group-hover:ml-2">
            네이버 블로그
          </span>
        </a>

        {/* 인스타그램 — 준비 중 */}
        <div
          className="group flex items-center gap-0 hover:gap-3 overflow-hidden transition-all duration-300 bg-white/70 border border-stone-200 rounded-full cursor-not-allowed opacity-60"
          style={{ height: "40px", paddingLeft: "10px", paddingRight: "10px" }}
          title="준비 중"
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-instagram-line text-stone-500 text-base" />
          </div>
          <span className="text-xs font-bold text-stone-500 whitespace-nowrap max-w-0 group-hover:max-w-xs overflow-hidden transition-all duration-300 group-hover:ml-2">
            준비 중
          </span>
        </div>

        {/* 유튜브 — 준비 중 */}
        <div
          className="group flex items-center gap-0 hover:gap-3 overflow-hidden transition-all duration-300 bg-white/70 border border-stone-200 rounded-full cursor-not-allowed opacity-60"
          style={{ height: "40px", paddingLeft: "10px", paddingRight: "10px" }}
          title="준비 중"
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-youtube-line text-stone-500 text-base" />
          </div>
          <span className="text-xs font-bold text-stone-500 whitespace-nowrap max-w-0 group-hover:max-w-xs overflow-hidden transition-all duration-300 group-hover:ml-2">
            준비 중
          </span>
        </div>

        <div className="w-px h-6 bg-stone-300 mx-auto mt-1" />
        <p className="text-[9px] font-bold text-stone-400 tracking-widest uppercase text-center">Follow</p>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 w-full pt-24 pb-16">
        <div className="max-w-xl">
          {/* Same-day install badge */}
          <div className="mb-6">
            <div className="inline-flex items-center gap-1.5 mb-3">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" style={{ animation: "pulse 2s infinite" }} />
              <span className="text-xs font-bold text-stone-500 tracking-widest uppercase">당일 설치 가능 지역</span>
            </div>
            {/* Region Tabs */}
            <div className="flex items-center gap-2 flex-wrap">
              {AREAS.map((area) => (
                <button
                  key={area.region}
                  onClick={() => handleAreaClick(area.region)}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold border transition-all whitespace-nowrap cursor-pointer ${
                    activeArea === area.region
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white/80 text-stone-700 border-stone-300 hover:border-stone-500"
                  }`}
                >
                  {area.region}
                  <i className={`text-[10px] transition-transform ${activeArea === area.region ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
                </button>
              ))}
            </div>

            {/* Districts Dropdown */}
            {activeArea && (
              <div className="mt-3 bg-white/90 border border-stone-200 rounded-2xl px-4 py-3 max-w-sm">
                <p className="text-[10px] font-bold text-stone-400 tracking-widest uppercase mb-2">{activeArea} 전 지역 · {activeDistricts.length}개</p>
                <div className="flex flex-wrap gap-1.5">
                  {activeDistricts.map((d) => (
                    <span
                      key={d}
                      className="text-[11px] font-medium text-stone-700 bg-stone-100 rounded-full px-2.5 py-0.5"
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-5xl md:text-7xl font-black text-stone-900 leading-none mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
            도어락은<br />
            <em className="not-italic text-stone-400" style={{ fontWeight: 300, fontStyle: 'italic' }}>도어준</em>
          </h1>

          <p className="text-base md:text-lg text-stone-600 leading-relaxed mb-10 max-w-sm">
            최첨단 보안 기술과 프리미엄 디자인의 만남.<br />
            당신의 집을 더 안전하고 스마트하게.
          </p>

          {/* CTAs */}
          <div className="flex flex-col gap-3 mb-2">
            {/* Phone CTA — 메인 강조 */}
            <a
              href="tel:01097939490"
              className="group inline-flex items-center gap-4 bg-stone-900 hover:bg-stone-700 transition-colors rounded-2xl px-6 py-4 w-fit cursor-pointer"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl group-hover:bg-white/20 transition-colors flex-shrink-0">
                <i className="ri-phone-fill text-white text-lg" />
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-widest uppercase text-white/50 mb-0.5">무료 상담 전화</p>
                <p className="text-xl font-black text-white tracking-wide" style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "0.04em" }}>
                  010-9793-9490
                </p>
              </div>
              <div className="ml-2 w-7 h-7 flex items-center justify-center bg-white/10 rounded-full group-hover:bg-white/20 transition-colors flex-shrink-0">
                <i className="ri-arrow-right-line text-white/70 text-sm" />
              </div>
            </a>

            {/* Secondary CTAs */}
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 border border-stone-300 text-stone-700 text-sm font-semibold px-6 py-2.5 rounded-full hover:border-stone-500 hover:bg-white/60 transition-colors whitespace-nowrap cursor-pointer"
              >
                제품 보러가기
                <i className="ri-arrow-right-line text-xs" />
              </Link>
              <div className="relative">
                {/* 뱃지 */}
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-emerald-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full whitespace-nowrap tracking-wide z-10">
                  <span className="w-1 h-1 rounded-full bg-white/80 animate-pulse inline-block" />
                  24시간 AI 응답
                </span>
                <button
                  onClick={openAIChat}
                  className="inline-flex items-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-800 text-sm font-bold px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-robot-2-line text-sm" />
                  </div>
                  AI 상담 시작하기
                </button>
              </div>
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-send-line text-xs" />
                문의 남기기
              </button>
            </div>
          </div>

          {showModal && <InstallInquiryModal onClose={() => setShowModal(false)} />}

          {/* Stats */}
          <div className="flex items-center gap-8 mt-14 pt-8 border-t border-stone-200">
            {[
              { value: "15,000+", label: "설치 완료" },
              { value: "99.8%", label: "보안 신뢰도" },
              { value: "1년", label: "품질 보증" },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-2xl font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{stat.value}</p>
                <p className="text-xs text-stone-500 mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* SNS 위젯 — 모바일 전용 가로 배치 */}
          <div className="flex lg:hidden items-center gap-3 mt-8 pt-6 border-t border-stone-200">
            <p className="text-[9px] font-bold text-stone-400 tracking-widest uppercase mr-1">Follow</p>
            {/* 네이버 블로그 */}
            <a
              href="https://blog.naver.com/qordudwns12"
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-1.5 bg-white/90 border border-stone-200 rounded-full px-3 py-1.5 cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-article-line text-emerald-600 text-sm" />
              </div>
              <span className="text-xs font-bold text-stone-700 whitespace-nowrap">블로그</span>
            </a>
            {/* 인스타그램 — 준비 중 */}
            <div
              className="flex items-center gap-1.5 bg-white/60 border border-stone-200 rounded-full px-3 py-1.5 opacity-50 cursor-not-allowed"
              title="준비 중"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-instagram-line text-stone-500 text-sm" />
              </div>
              <span className="text-xs font-bold text-stone-500 whitespace-nowrap">인스타</span>
            </div>
            {/* 유튜브 — 준비 중 */}
            <div
              className="flex items-center gap-1.5 bg-white/60 border border-stone-200 rounded-full px-3 py-1.5 opacity-50 cursor-not-allowed"
              title="준비 중"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-youtube-line text-stone-500 text-sm" />
              </div>
              <span className="text-xs font-bold text-stone-500 whitespace-nowrap">유튜브</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
